// pushNotificationUrl.js — browser-side mirror of the credential-field rules.
//
// This is for FEEDBACK, not for safety. api-server validates the same values at
// the request boundary and push-notification-worker validates them again after
// decrypting, and those two are the ones that matter — nothing here is trusted
// by anything. Its whole job is telling someone they pasted the wrong thing
// before they click Save.
//
// It deliberately mirrors `shared-push-notification`'s rules and its wording,
// so a value this accepts is not then rejected by the server with a different
// explanation. If the Rust rules change, change these; a drift shows up as a
// confusing double message, not as a hole.
//
// The host rules themselves live in `pushNotificationVendors.js` rather than in
// a second copy here -- one vendor table, so adding a vendor cannot leave this
// file behind.

import { vendorByKey } from "./pushNotificationVendors.js";

// Mirrors MAX_URL_LEN.
export const MAX_URL_LEN = 2048;

// Mirrors MAX_INSTANCE_LABEL_LEN — the DNS limit for one label.
export const MAX_INSTANCE_LABEL_LEN = 63;

// Mirrors DENIED_INSTANCE_LABELS.
const DENIED_INSTANCE_LABELS = ["localhost", "internal", "metadata", "local"];

/**
 * Check one credential field for a vendor.
 *
 * @param {string} vendorKey - a vendor key, e.g. `"slack"`
 * @param {object} field - an entry from the vendor's `fields`
 * @param {string} raw - what the user typed
 * @returns {string|null} a message to show, or null when it looks fine
 */
export function validateCredentialField(vendorKey, field, raw) {
    const spec = vendorByKey(vendorKey);
    if (spec === undefined) return "Choose a destination type.";
    const trimmed = String(raw ?? "").trim();
    // The label verbatim, NOT lowercased. The labels carry brand names and
    // initialisms -- "Slack webhook URL" -- and lowercasing them produced
    // "Enter the slack webhook url.", which reads as a typo in our own product.
    if (!trimmed) return `Enter the ${field.label}.`;

    switch (field.kind) {
        case "destination_url":
            return validateDestinationUrl(vendorKey, trimmed);
        case "instance_label":
            return validateInstanceLabel(trimmed);
        case "splunk_cloud_host":
            return validateSplunkCloudHost(spec.destination, trimmed);
        case "secret":
            return validateSecret(spec.authScheme, trimmed);
        default:
            return null;
    }
}

/**
 * Check a shared secret.
 *
 * Whether it is the RIGHT key is not knowable here -- the server finds that out
 * on the first send and the delivery status says so. What IS knowable is whether
 * it can become an HTTP header value at all, which is what `header_safe` refuses
 * in Rust: visible ASCII only. Without this, a key carrying a space, a tab or a
 * smart quote copied out of a docs page is accepted by the browser and refused
 * by api-server, which is the confusing double message this file exists to
 * prevent.
 *
 * Any scheme prefix is stripped first, in the same order as `Credential::parse`
 * (trim, strip, then check), so a pasted `Api-Key ` is not read as a space in
 * the middle of the value. It comes from the vendor rather than being a constant
 * here, because `normalize_api_key` strips only what `Vendor::auth_scheme`
 * names: a vendor with no scheme stores the value exactly as typed, and
 * stripping one on this side would accept a key the server then refuses.
 *
 * Takes the SCHEME rather than a vendor key, mirroring `normalize_api_key`:
 * whether anything is stripped is a property of the scheme, so both branches
 * stay reachable in a build where no shipped vendor declares one.
 *
 * @param {string|undefined} scheme - the vendor's `authScheme`, if it has one
 * @param {string} trimmed - what the user typed, already trimmed and non-blank
 * @returns {string|null} a message to show, or null when it looks fine
 */
export function validateSecret(scheme, trimmed) {
    const prefix = scheme ? `${scheme.toLowerCase()} ` : null;
    const value =
        prefix && trimmed.toLowerCase().startsWith(prefix)
            ? trimmed.slice(prefix.length).trimStart()
            : trimmed;
    // `is_ascii_graphic`: 0x21..=0x7e. Excludes the space, every control
    // character, and everything above ASCII.
    // eslint-disable-next-line no-control-regex
    if (/[^\x21-\x7e]/.test(value)) {
        // The server's wording for `CredentialError::InvalidField`, so the two
        // cannot disagree about the same value.
        return "That credential contains characters that aren't allowed. Check for a stray line break or a copied space.";
    }
    return null;
}

/**
 * Check a destination URL for a vendor whose credential is one.
 *
 * @param {string} vendorKey - a vendor key, e.g. `"slack"`
 * @param {string} raw - what the user typed
 * @returns {string|null} a message to show, or null when it looks fine
 */
export function validateDestinationUrl(vendorKey, raw) {
    const spec = vendorByKey(vendorKey);
    if (spec === undefined) return "Choose a destination type.";
    return validateForDestination(spec.destination, raw);
}

/**
 * Validate a URL against a destination shape directly.
 *
 * Split out for the reason `validate_for_destination` is split out in
 * `url.rs`, and mirroring it: the checks are a property of the SHAPE, not of
 * the vendor that declares it, so every shape stays testable on its own. A
 * `pathMatch` of `exact` that silently behaved as a prefix would be
 * indistinguishable from a correct one at the call site, and no shipped vendor
 * exercises it.
 *
 * @param {import("./pushNotificationVendors.js").PushDestination} destination
 * @param {string} raw
 * @returns {string|null} A message to show, or null when the URL is acceptable.
 */
export function validateForDestination(destination, raw) {
    if (destination.kind !== "webhook" && destination.kind !== "powerAutomateWorkflow") {
        return "This destination doesn't take a URL.";
    }
    const trimmed = String(raw ?? "").trim();

    if (!trimmed) return "Enter a webhook URL.";
    if (trimmed.length > MAX_URL_LEN) return "That URL is too long.";
    // Before parsing: `new URL()` punycodes an IDN host, so a homograph would
    // pass an ASCII check made afterwards.
    // eslint-disable-next-line no-control-regex
    if (/[^\x20-\x7e]/.test(trimmed)) {
        return "That URL contains characters that aren't allowed.";
    }

    let url;
    try {
        url = new URL(trimmed);
    } catch {
        return "That doesn't look like a URL.";
    }

    if (url.protocol !== "https:") return "The URL must start with https://.";
    // `https://hooks.slack.com@evil.example/` has host `evil.example` — a real
    // bypass, not hygiene.
    if (url.username || url.password) return "Remove the username and password from the URL.";
    // An explicit `:443` reads as no port here, as it does in Rust's `url`.
    if (url.port) return "Remove the port number from the URL.";
    if (url.hash) return "Remove the fragment from the URL.";
    if (destination.kind === "powerAutomateWorkflow") return validateWorkflowUrl(destination, url);
    if (url.search) return "Remove the query string from the URL.";
    // Exact match against each pinned host. Deliberately not a suffix test:
    // matching `.slack.com` would accept any Slack subdomain.
    const host = url.hostname.toLowerCase();
    if (!destination.hosts.some((h) => h.toLowerCase() === host)) {
        return "That URL isn't on this provider's domain.";
    }
    // How much of the path is ours to pin is the vendor's property, mirroring
    // `PathRule` in Rust. Checking a full method path as a prefix would accept a
    // URL with anything appended, which the server then refuses -- the
    // accepted-then-rejected split this file exists to avoid.
    const pathOk =
        destination.pathMatch === "exact"
            ? url.pathname === destination.pathPrefix
            : url.pathname.startsWith(destination.pathPrefix);
    if (!pathOk) {
        return "That doesn't look like a webhook URL for this provider.";
    }
    return null;
}

// Mirror `url.rs`'s workflow constants; the drift test pins them.
export const WORKFLOW_PATH_PREFIX = "/powerautomate/automations/direct/";
export const WORKFLOW_PATH_SUFFIX = "/triggers/manual/paths/invoke";
export const WORKFLOW_RUN_PERMISSION = "/triggers/manual/run";
export const MIN_WORKFLOW_SIGNATURE_LEN = 43;

const WORKFLOW_QUERY_KEYS = ["api-version", "sp", "sv", "sig"];
const INCOMPLETE_WORKFLOW_URL =
    "That isn't a complete workflow URL. Copy it again from the workflow, including the part after the question mark.";

/**
 * The workflow id in `<prefix>[cu/<unit>/]workflows/<id><suffix>[/]`, or "" when
 * the path is not that shape. Mirrors `validate_workflow_path`.
 *
 * @param {string} path
 * @returns {string}
 */
function workflowIdFromPath(path) {
    if (!path.startsWith(WORKFLOW_PATH_PREFIX)) return "";
    let rest = path.slice(WORKFLOW_PATH_PREFIX.length);
    if (rest.startsWith("cu/")) {
        const slash = rest.indexOf("/", "cu/".length);
        if (slash < 0 || !/^[A-Za-z0-9-]{1,64}$/.test(rest.slice("cu/".length, slash))) return "";
        rest = rest.slice(slash + 1);
    }
    if (!rest.startsWith("workflows/")) return "";
    rest = rest.slice("workflows/".length);
    if (rest.endsWith("/")) rest = rest.slice(0, -1);
    if (!rest.endsWith(WORKFLOW_PATH_SUFFIX)) return "";
    return rest.slice(0, rest.length - WORKFLOW_PATH_SUFFIX.length);
}

/**
 * The `Destination::PowerAutomateWorkflow` rules: two fixed-format labels under
 * the apex, one workflow's trigger path, and exactly the signed query.
 *
 * @param {import("./pushNotificationVendors.js").PushDestination} destination
 * @param {URL} url already past the shared checks
 * @returns {string|null}
 */
function validateWorkflowUrl(destination, url) {
    const labels = url.hostname.toLowerCase().split(".");
    const environment = labels[0]?.startsWith("default") ? labels[0].slice("default".length) : labels[0];
    if (
        labels.length < 3 ||
        labels.slice(2).join(".") !== destination.apex ||
        !/^[0-9a-f]{30}$/.test(environment ?? "") ||
        !/^[0-9a-f]{2}$/.test(labels[1])
    ) {
        return "That URL isn't on this provider's domain.";
    }
    if (!/^[0-9a-fA-F]{32}$/.test(workflowIdFromPath(url.pathname))) {
        return "That doesn't look like a webhook URL for this provider.";
    }
    // Parsed by hand rather than through URLSearchParams, which decodes `+`
    // and `%XX` and would judge different bytes from the ones sent.
    const query = url.search.slice(1);
    if (!query) return INCOMPLETE_WORKFLOW_URL;
    const values = {};
    for (const pair of query.split("&")) {
        const at = pair.indexOf("=");
        if (at < 0) return INCOMPLETE_WORKFLOW_URL;
        const key = pair.slice(0, at);
        if (!WORKFLOW_QUERY_KEYS.includes(key) || key in values) return INCOMPLETE_WORKFLOW_URL;
        values[key] = pair.slice(at + 1);
    }
    if (WORKFLOW_QUERY_KEYS.some((key) => !(key in values))) return INCOMPLETE_WORKFLOW_URL;
    const wellFormed =
        /^[0-9A-Za-z.-]{1,32}$/.test(values["api-version"]) &&
        /^[0-9.]{1,8}$/.test(values.sv) &&
        values.sp.replace(/%2F/gi, "/") === WORKFLOW_RUN_PERMISSION &&
        values.sig.length >= MIN_WORKFLOW_SIGNATURE_LEN &&
        /^[A-Za-z0-9_-]+$/.test(values.sig);
    return wellFormed ? null : INCOMPLETE_WORKFLOW_URL;
}

/**
 * Check an instance label. The endpoint is built from it against a pinned
 * apex, so this is the only part of the URL a customer supplies.
 *
 * @param {string} raw - what the user typed
 * @returns {string|null} a message to show, or null when it looks fine
 */
export function validateInstanceLabel(raw) {
    const label = String(raw ?? "")
        .trim()
        .toLowerCase();
    const invalid =
        "Enter just the instance name from your sign-in address, using letters, numbers and hyphens.";

    if (!label) return "Enter an instance name.";
    if (label.length > MAX_INSTANCE_LABEL_LEN) return invalid;
    if (!/^[a-z0-9-]+$/.test(label)) return invalid;
    if (label.startsWith("-") || label.endsWith("-")) return invalid;
    // Punycode is ASCII, so nothing above catches a homograph.
    if (label.startsWith("xn--")) return invalid;
    if (DENIED_INSTANCE_LABELS.includes(label)) return invalid;
    return null;
}

// Mirror SPLUNK_CLOUD_AWS_PREFIX and SPLUNK_CLOUD_GCP_LABEL in `url.rs`.
export const SPLUNK_CLOUD_AWS_PREFIX = "http-inputs-";
export const SPLUNK_CLOUD_GCP_LABEL = "http-inputs";

/**
 * Tidy what someone pasted into a host field into a bare host.
 *
 * Mirrors `normalize_customer_host`. People paste the whole endpoint their
 * vendor's console shows them into a field asking for a host; normalizing it
 * rather than rejecting it is what `normalize_api_key` already does for a
 * pasted scheme prefix.
 *
 * @param {string} raw - what the user typed
 * @returns {string} the bare host, lowercased
 */
export function normalizeCustomerHost(raw) {
    const trimmed = String(raw ?? "")
        .trim()
        .toLowerCase();
    const withoutScheme = trimmed.replace(/^https?:\/\//, "");
    return withoutScheme.split("/")[0];
}

/**
 * Check a Splunk Cloud collector host.
 *
 * Mirrors the `Destination::SplunkCloudCollector` arm of
 * `validate_for_destination`: `http-inputs-<stack>.<apex>` on AWS or
 * `http-inputs.<stack>.<apex>` on Google Cloud, on the default port.
 *
 * @param {import("./pushNotificationVendors.js").PushDestination} destination
 * @param {string} raw - what the user typed
 * @returns {string|null} a message to show, or null when it looks fine
 */
export function validateSplunkCloudHost(destination, raw) {
    if (destination.kind !== "splunkCloud") return "This destination doesn't take a host.";
    const host = normalizeCustomerHost(raw);
    if (!host) return "Enter a host.";
    if (host.length > MAX_URL_LEN) return "That host is too long.";
    // Before parsing: `new URL()` punycodes an IDN host, so a homograph would
    // pass an ASCII check made afterwards.
    // eslint-disable-next-line no-control-regex
    if (/[^\x20-\x7e]/.test(host)) return "That host contains characters that aren't allowed.";

    let url;
    try {
        url = new URL(`https://${host}${destination.path}`);
    } catch {
        return "That doesn't look like a host.";
    }
    if (url.pathname !== destination.path || url.search || url.hash || url.username) {
        return "Enter just the host, without a path.";
    }
    // `URL` drops an explicit :443, so any port left is one the collector does not use.
    if (url.port !== "") return "Remove the port. Splunk Cloud's collector uses the default port.";

    const notSplunkCloud = `Enter your Splunk Cloud collector host, such as ${SPLUNK_CLOUD_AWS_PREFIX}<stack>.${destination.apex}.`;
    const suffix = `.${destination.apex}`;
    if (!url.hostname.endsWith(suffix)) return notSplunkCloud;
    const collector = url.hostname.slice(0, -suffix.length);
    const dot = collector.indexOf(".");
    let stack;
    if (dot === -1) {
        if (!collector.startsWith(SPLUNK_CLOUD_AWS_PREFIX)) return notSplunkCloud;
        stack = collector.slice(SPLUNK_CLOUD_AWS_PREFIX.length);
    } else {
        if (collector.slice(0, dot) !== SPLUNK_CLOUD_GCP_LABEL) return notSplunkCloud;
        stack = collector.slice(dot + 1);
    }
    return validateInstanceLabel(stack) === null ? null : notSplunkCloud;
}
